/**
 * Decodes an ArrayBuffer to a string, auto-detecting the encoding.
 *
 * Strategy:
 * 1. Check for BOM (UTF-8, UTF-16 LE/BE) and use the indicated encoding.
 * 2. Try UTF-8 with `fatal: true` — if the bytes are valid UTF-8, use that.
 * 3. Fall back to ISO-8859-1 (Latin-1), which maps every byte 0x00–0xFF
 *    and never fails. This covers most Western European CSV exports.
 */
export function decodeTextFromBuffer(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);

    // UTF-8 BOM: EF BB BF
    if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
        return new TextDecoder('utf-8').decode(buffer);
    }

    // UTF-16 LE BOM: FF FE — skip the 2-byte BOM via Uint8Array offset
    if (bytes[0] === 0xff && bytes[1] === 0xfe) {
        return new TextDecoder('utf-16le').decode(new Uint8Array(buffer, 2));
    }

    // UTF-16 BE BOM: FE FF — skip the 2-byte BOM via Uint8Array offset
    if (bytes[0] === 0xfe && bytes[1] === 0xff) {
        return new TextDecoder('utf-16be').decode(new Uint8Array(buffer, 2));
    }

    // Try UTF-8 — throws on invalid byte sequences
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
    } catch {
        // Not valid UTF-8, fall back to ISO-8859-1
        return new TextDecoder('iso-8859-1').decode(buffer);
    }
}
